import type { ListData } from '@metapic-apps/shared'
import { type Ref, ref, toValue, watch } from 'vue'

import { normalizeBlocklist, serializeBlocklistFormData } from '@/lib/adapters/blocklist.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { makeQueryString } from '@/lib/helpers/query.helpers'
import { request } from '@/lib/services'
import type {
  BlocklistFormType,
  BlockListItemType,
  BlocklistServerItemType,
} from '@/lib/types/blocklist.types'
import type { FilterType } from '@/lib/types/common.types'
import { useErrorStore } from '@/stores/error'

export const useAddEntriesToBlocklist = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const addToBlocklist = async (advertiserId: number, formData: BlocklistFormType) => {
    try {
      isLoading.value = true
      error.value = null

      await request.post(
        `/v2/advertisers/${advertiserId}/blocklist`,
        serializeBlocklistFormData(formData),
      )
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    addToBlocklist,
    isLoading,
    error,
  }
}

export const useRemoveEntriesFromBlocklist = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const removeFromBlocklist = async (advertiserId: number, targetId: number) => {
    try {
      isLoading.value = true
      error.value = null

      await request.delete(`/v2/advertisers/${advertiserId}/blocklist/${targetId}`)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    removeFromBlocklist,
    isLoading,
    error,
  }
}

export const useGetBlocklist = (advertiserId: number, filterRef: Ref<FilterType>) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const data = ref<BlockListItemType[] | null>(null)
  const totalPages = ref<number>(0)
  const links = ref<{ first?: string; last?: string; prev?: string; next?: string }>({})

  const getBlocklist = async (query: string) => {
    try {
      isLoading.value = true
      error.value = null

      const response = await request.get<ListData<BlocklistServerItemType>>(
        `/v2/advertisers/${advertiserId}/blocklist?${query}`,
      )
      data.value = normalizeBlocklist(response.data.data)
      totalPages.value = response.data.meta.last_page
      links.value = response.data.links
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    filterRef,
    async () => {
      const query = makeQueryString(toValue(filterRef))

      if (query) {
        await getBlocklist(query)
      }
    },
    { immediate: true, deep: true },
  )

  return {
    data,
    links,
    totalPages,
    isLoading,
    error,
  }
}
